/**
 * ApiService
 * Handles all API communication with the backend
 */
import { fixEncoding } from '../utils/fixEncoding.js';

const ENV_API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').trim();

const normalizeApiBaseUrl = (baseUrl) => {
  if (!baseUrl) return '/api';
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // In local/browser Docker development we always want the nginx /api proxy.
    if (host === 'localhost' || host === '127.0.0.1') {
      return '/api';
    }
  }

  return ENV_API_BASE_URL || '/api';
};

export const API_BASE_URL = normalizeApiBaseUrl(resolveApiBaseUrl());

/**
 * Recursively fix encoding on all string values in a JSON response.
 * Handles objects, arrays, and nested structures.
 */
function fixResponseEncoding(data) {
  if (typeof data === 'string') return fixEncoding(data);
  if (Array.isArray(data)) return data.map(fixResponseEncoding);
  if (data && typeof data === 'object') {
    const fixed = {};
    for (const [key, value] of Object.entries(data)) {
      fixed[key] = fixResponseEncoding(value);
    }
    return fixed;
  }
  return data;
}

class ApiService {
  static async delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static shouldRetryRequest(options = {}, response = null) {
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET') return false;
    if (!response) return false;
    return response.status >= 500;
  }

  static async handleResponse(response) {
    // Check for refreshed token in response headers
    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) {
      localStorage.setItem('authToken', refreshedToken);
    }

    // Handle session expiration
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.code === 'SESSION_EXPIRED' || errorData.code === 'TOKEN_EXPIRED') {
        // Clear token and redirect to login
        localStorage.removeItem('authToken');
        window.dispatchEvent(new Event('sessionExpired'));
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`
      );
      // Attach validation details if present
      if (errorData.details) {
        error.details = errorData.details;
      }
      throw error;
    }

    const data = await response.json();
    return fixResponseEncoding(data);
  }

  /**
   * Get the base URL for API requests
   */
  static getBaseUrl() {
    return '';
  }

  /**
   * Get auth token from storage
   */
  static getAuthToken() {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  }

  /**
   * Generic fetch wrapper with error handling and authentication
   * @param {string} endpoint - API endpoint path
   * @param {Object} options - Fetch options
   * @returns {Promise<any>}
   */
  static async fetchAPI(endpoint, options = {}) {
    const primaryUrl = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authentication token if available
    const token = this.getAuthToken();
    if (token) {
      defaultOptions.headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      let response = await fetch(primaryUrl, defaultOptions);

      // Retry once for transient startup/network issues on idempotent GET requests.
      if (this.shouldRetryRequest(defaultOptions, response)) {
        await this.delay(200);
        response = await fetch(primaryUrl, defaultOptions);
      }

      return await this.handleResponse(response);
    } catch (error) {
      // If configured base URL is unreachable, retry once against same-origin /api.
      const canRetryWithProxy =
        API_BASE_URL !== '/api' &&
        error &&
        error.name === 'TypeError';

      if (canRetryWithProxy) {
        try {
          const fallbackResponse = await fetch(`/api${endpoint}`, defaultOptions);
          return await this.handleResponse(fallbackResponse);
        } catch (fallbackError) {
          console.error(`API fallback request failed for ${endpoint}:`, fallbackError);
        }
      }

      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * GET request
   */
  static async get(endpoint) {
    return this.fetchAPI(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  static async post(endpoint, body) {
    return this.fetchAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  /**
   * PUT request
   */
  static async put(endpoint, body) {
    return this.fetchAPI(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  /**
   * DELETE request
   */
  static async delete(endpoint) {
    return this.fetchAPI(endpoint, { method: 'DELETE' });
  }

  /**
   * Get sessions filtered by tab and optional event date
   * @param {string} tab - Tab type: 'live', 'recorded', or 'archived'
   * @param {string|null} eventDate - Optional event date for archived sessions
   * @returns {Promise<{sessions: Object[], lightweightMode: boolean}>}
   */
  static async getSessions(tab, eventDate = null) {
    const params = new URLSearchParams({ tab });
    
    if (eventDate) {
      params.append('eventDate', eventDate);
    }

    const response = await this.fetchAPI(`/sessions?${params.toString()}`);
    return {
      sessions: response.data || [],
      lightweightMode: response.lightweightMode || false
    };
  }

  /**
   * Get all quarterly events
   * @returns {Promise<Object[]>}
   */
  static async getQuarterlyEvents() {
    const response = await this.fetchAPI('/sessions/events');
    return response.data || [];
  }

  /**
   * Get the active event
   * @returns {Promise<Object|null>}
   */
  static async getActiveEvent() {
    const response = await this.fetchAPI('/sessions/active-event');
    return response.data || null;
  }

  /**
   * Get all business segments
   * @returns {Promise<Object[]>}
   */
  static async getSegments() {
    const response = await this.fetchAPI('/sessions/segments');
    return response.data || [];
  }

  /**
   * Get all session categories
   * @returns {Promise<Object[]>}
   */
  static async getCategories() {
    const response = await this.fetchAPI('/sessions/categories');
    return response.data || [];
  }

  /**
   * Get public settings (segment hub URLs, video tour URL)
   * @returns {Promise<Object>}
   */
  static async getPublicSettings() {
    const response = await this.fetchAPI('/settings/public');
    return response.data || {};
  }

  /**
   * Get a single session by ID
   * @param {number} id - Session ID
   * @returns {Promise<Object>}
   */
  static async getSessionById(id) {
    const response = await this.fetchAPI(`/sessions/${id}`);
    return response.data;
  }

  /**
   * Upload Excel file with sessions for a specific segment
   * @param {File} file - Excel file
   * @param {string} segment - Business segment code
   * @param {string} endpoint - Optional custom endpoint (default: /admin/upload)
   * @returns {Promise<Object>}
   */
  static async uploadExcel(file, segment, endpoint = '/admin/upload') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('segment', segment);

    const url = `${API_BASE_URL}${endpoint}`;

    try {
      const headers = {};
      
      // Add authentication token if available
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary
      });

      // Check for refreshed token
      const refreshedToken = response.headers.get('X-Refreshed-Token');
      if (refreshedToken) {
        localStorage.setItem('authToken', refreshedToken);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Excel upload failed:', error);
      throw error;
    }
  }

  /**
   * Get available segments with active surveys
   * @returns {Promise<string[]>}
   */
  static async getSurveySegments() {
    return this.fetchAPI('/survey/segments');
  }

  /**
   * Get survey questions for a specific segment
   * @param {string} segment - Business segment code
   * @returns {Promise<Object[]>}
   */
  static async getSurveyQuestions(segment) {
    return this.fetchAPI(`/survey/questions/${segment}`);
  }

  /**
   * Submit survey response
   * @param {string} segment - Business segment code
   * @param {string} sessionId - Unique session identifier
   * @param {Array} responses - Array of {questionId, answer}
   * @returns {Promise<Object>}
   */
  static async submitSurvey(segment, sessionId, responses, eventId = null) {
    return this.fetchAPI('/survey/submit', {
      method: 'POST',
      body: JSON.stringify({ segment, sessionId, responses, eventId }),
    });
  }

  /**
   * Check if session has already submitted for a segment
   * @param {string} segment - Business segment code
   * @param {string} sessionId - Unique session identifier
   * @returns {Promise<boolean>}
   */
  static async checkSurveySubmission(segment, sessionId, eventId = null) {
    let url = `/survey/check/${segment}/${sessionId}`;
    if (eventId) url += `?eventId=${eventId}`;
    const result = await this.fetchAPI(url);
    return result.hasSubmitted;
  }

  /**
   * Export survey responses (admin)
   * @param {string} segment - Business segment code
   * @param {string} format - Export format ('csv' or 'json')
   * @param {string} startDate - Optional start date
   * @param {string} endDate - Optional end date
   * @returns {Promise<any>}
   */
  static async exportSurveyResponses(segment, format = 'csv', startDate = null, endDate = null) {
    const params = new URLSearchParams({ format });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    return this.fetchAPI(`/admin/survey/export/${segment}?${params.toString()}`);
  }

  /**
   * Get survey statistics (admin)
   * @param {string} segment - Business segment code
   * @returns {Promise<Object>}
   */
  static async getSurveyStatistics(segment) {
    return this.fetchAPI(`/admin/survey/stats/${segment}`);
  }
}

export default ApiService;
